import { parse as parseDate } from 'date-fns'
import Image from 'next/image'
import { useRouter } from 'next/router'

import type { ButtonProps } from '@wartek-id/button-v1'
import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'

import { useAPIUpdateState } from 'api/kinerja/useAPIKinerjaKS'
import { formatDate } from 'utils/datetime'
import useSchoolContext from 'app/KinerjaKSPage/hooks/useSchoolContext'
import CardPelaksanaan from 'app/KinerjaKSPage/components/CardPelaksanaan'
import CardProgressPenilaian from 'app/KinerjaKSPage/components/CardProgressPenilaian'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import useSKPKSTrackEvent from 'app/KinerjaKSPage/hooks/useSKPKSTrackEvent'
import CardProgressPenilaianTahunan from 'app/KinerjaKSPage/components/CardProgressPenilaianTahunan'
import useToggleSdkContext from 'utils/useToogleSdk'
import { useApiGetRiwayatTahunanByYear } from 'api/kinerja/kepala-sekolah/riwayat'
import Before2025 from 'app/KinerjaShared/components/Before2025'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'

import type { Pelaksanaan } from 'types/kinerja-ks-pelaksanaan'
import type { Kinerja, KinerjaStatus } from 'types/kinerja'
import type { KinerjaKsStatus, School } from 'types/kinerja-ks'

interface CardKinerjaStatusProps {
  skpId: string
  status: KinerjaStatus
  onSuccessUpdateSKP: () => any
  dataPelaksanaan: Pelaksanaan
}

export const getStatusMap = (
  period: Kinerja['period'],
  isPelaksanaanDatePassed: boolean,
  pelaksanaanDate: Date,
  baseUrl: string,
  npsn?: School['npsn']
): Record<
  KinerjaKsStatus,
  {
    icon: string
    preTitle: JSX.Element
    title: string
    description: string | JSX.Element
    ctaText?: string
    ctaButtonColor?: ButtonProps['color']
    ctaHref?: string
  }
> => {
  return {
    NOT_FOUND: {
      icon: 'img-to-do-list-document.png',
      preTitle: <CardPeriodType withDate type="perencanaan" />,
      title: 'Mulai Perencanaan Kinerja',
      description:
        'Isi Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja dengan seksama. Setelah dikumpulkan, perencanaan tidak dapat diubah lagi. Pastikan perencanaan dikumpulkan sebelum batas waktu.',
      ctaText: 'Mulai',
      ctaButtonColor: 'blue',
      ctaHref: `${baseUrl}/konfirmasi-profil${!!npsn ? `?id=${npsn}` : ''}`,
    },
    DRAFT: {
      icon: 'img-to-do-list-document.png',
      preTitle: <CardPeriodType withDate type="perencanaan" />,
      title: 'Mulai Perencanaan Kinerja',
      description:
        'Isi Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja dengan seksama. Setelah dikumpulkan, perencanaan tidak dapat diubah lagi. Pastikan perencanaan dikumpulkan sebelum batas waktu.',
      ctaText: 'Lanjutkan',
      ctaButtonColor: 'blue',
      ctaHref: `${baseUrl}/perencanaan${!!npsn ? `?id=${npsn}` : ''}`,
    },
    READY: isPelaksanaanDatePassed
      ? {
          icon: 'img-assessment-indicator.png',
          preTitle: <CardPeriodType withDate type="pelaksanaan" />,
          title: 'Pelaksanaan Kinerja',
          description:
            'Berdasarkan perencanaan Anda, laksanakan Praktik Kinerja dan kumpulkan dokumen yang dibutuhkan.',
        }
      : {
          icon: 'img-time-document.png',
          preTitle: <CardPeriodType withDate type="perencanaan" />,
          title: 'Menunggu periode Pelaksanaan Kinerja',
          description: (
            <>
              Perencanaan Anda sudah otomatis disetujui. Anda dapat lanjut ke
              Pelaksanaan mulai dari{' '}
              <strong>{formatDate(pelaksanaanDate, 'd MMMM yyyy')}</strong>. Cek
              lagi halaman ini nanti, ya.
            </>
          ),
        },
  }
}

export default function CardKinerjaStatus({
  skpId,
  status,
  onSuccessUpdateSKP,
  dataPelaksanaan,
}: CardKinerjaStatusProps) {
  const toggleValue = useToggleSdkContext()
  const {
    periodNo: period,
    periodYear,
    period: periodData,
  } = usePeriodContext()

  const showVersion2 =
    !!toggleValue['kinerja-ks-dokumen-evaluasi-revamp-enabled']
  const showVersion2Tahunan =
    !!toggleValue['kinerja-ks-dokumen-evaluasi-tahunan-enabled']

  const pelaksanaanDate = periodData?.periodeKinerja?.find(
    ({ key }) => key === 'persiapan'
  )?.value
  const month = period === 2 ? 7 : 1
  const defaultPelaksanaanDate = new Date(new Date().getFullYear(), month, 1)

  const parsedPelaksanaanDate = pelaksanaanDate
    ? parseDate(pelaksanaanDate, 'yyyy-MM-dd', new Date())
    : defaultPelaksanaanDate

  const today = new Date()

  const isPelaksanaanDatePassed =
    today.getTime() >= parsedPelaksanaanDate.getTime()

  const { currentNpsn } = useSchoolContext()
  const { trackClickStartPlanning } = useSKPKSTrackEvent()
  const baseUrl = usePengelolaanKinerjaBaseUrl()

  const {
    icon: SelectedIcon,
    preTitle,
    title,
    description,
    ctaText,
    ctaButtonColor,
    ctaHref,
  } = getStatusMap(
    period,
    isPelaksanaanDatePassed,
    parsedPelaksanaanDate,
    baseUrl,
    currentNpsn
  )[status]

  const { isSuccess: isRiwayatTahunanSuccess, data: riwayatDataTahunan } =
    useApiGetRiwayatTahunanByYear(
      { year: `${periodYear}`, npsn: currentNpsn },
      {
        enabled:
          showVersion2 && showVersion2Tahunan && !!periodYear && !!currentNpsn,
      }
    )

  const hasData =
    isRiwayatTahunanSuccess && riwayatDataTahunan?.data?.length > 0

  const { mutateAsync: updateStateSKP, isLoading: isUpdatingStateSKP } =
    useAPIUpdateState()

  const isReadyForPelaksanaan = status === 'READY' && isPelaksanaanDatePassed
  const isDisabledCta = isReadyForPelaksanaan

  const router = useRouter()

  const handleCta = async () => {
    if (status === 'NOT_FOUND') {
      trackClickStartPlanning({
        screenName: 'KINERJA_KS_HOMEPAGE',
        kinerjaPlanningId: skpId,
      })
    }

    if (isReadyForPelaksanaan) {
      await updateStateSKP(
        { skpId },
        {
          onSuccess() {
            onSuccessUpdateSKP()
          },
        }
      )
    } else {
      router.push(ctaHref)
    }
  }

  if (isPelaksanaanDatePassed && dataPelaksanaan) {
    return (
      <>
        {/* To only be displayed if both toggles are enabled */}
        {hasData && (
          <CardProgressPenilaianTahunan
            isAvailable
            year={periodYear}
            targetUrl={`${baseUrl}/hasil-evaluasi?id=${currentNpsn}&tab=3`}
          />
        )}

        <CardProgressPenilaian
          npsn={currentNpsn}
          progressPercentage={dataPelaksanaan.progressPercentage}
        />
        <CardPelaksanaan data={dataPelaksanaan} />
      </>
    )
  }

  return (
    <Card>
      <div className="flex gap-6 p-6">
        <div className="w-[84px] h-[84px] relative">
          {/* Prevent user select */}
          <div className="absolute inset-0 z-10">&nbsp;</div>

          <Image
            alt=""
            src={['/illustrations', SelectedIcon].join('/')}
            width={84}
            height={84}
            unoptimized={true}
          />
        </div>
        <div className="flex-1 ml-6 flex flex-col gap-1 justify-center">
          <Before2025>
            <Text
              variant="subheading"
              color="informational"
              className="!text-blue-60 !uppercase tracking-[0.5px]"
            >
              {preTitle}
            </Text>
          </Before2025>
          <Text variant="heading-lg">{title}</Text>
          <Text color="subdued">{description}</Text>
        </div>
        {ctaText && (
          <div className="min-w-[240px]">
            <Button
              size="lg"
              fullWidth
              color={ctaButtonColor}
              disabled={isDisabledCta || isUpdatingStateSKP}
              isLoading={isUpdatingStateSKP}
              onClick={handleCta}
              data-testid="cta-button"
            >
              {ctaText}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
